import React from "react";
import { Badge, Tooltip } from "flowbite-react";
import Chart from "react-apexcharts";

/**
 * ConcentracaoRecebimentoPJQuery
 *
 * Props:
 * - data?: { indicador?: number | string | null }
 * - companyName?: string
 * - periodLabel?: string
 * - totalRecebimentos?: number
 * - topCompanyAmount?: number
 * - className?: string
 * - layout?: "full" | "half"   // "half" quando renderizar em 2 colunas
 */
export default function ConcentracaoRecebimentoPJQuery({
  data,
  companyName = "Empresa com maior representatividade",
  periodLabel,
  totalRecebimentos,
  topCompanyAmount,
  className = "",
  layout = "full",
}) {
  const isHalf = layout === "half";

  // ===== Guarda: sem dados suficientes =====
  if (data?.indicador == null) {
    return (
      <div>
        <div
          className={`w-full rounded-md border border-gray-200 p-5 mt-4 ${className}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Concentração de Recebimentos
              </h3>
              <p className="mt-0.5 text-sm text-gray-500">
                {periodLabel
                  ? `Período: ${periodLabel}`
                  : "Indicador indisponível"}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mt-4">
            <div className="flex gap-3">
              <span className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-amber-500" />
              <div className="text-sm leading-relaxed text-amber-900">
                Não há informações suficientes para exibir este indicador.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Cálculos diretos =====
  const raw = Number(data?.indicador);
  const v = Number.isFinite(raw) ? clamp(raw, 0, 1) : 0; // 0..1
  const pct = v * 100;

  const tier = getTier(v);
  const { colorHex } = tierColors(tier);

  const series = [v, 1 - v];

  const chartOptions = {
    chart: {
      type: "pie",
      toolbar: { show: false },
      sparkline: { enabled: true },
    },
    labels: [companyName, "Outros"],
    legend: {
      show: true,
      position: "bottom",
      fontSize: "12px",
      markers: { width: 10, height: 10, offsetX: -2 },
      itemMargin: { horizontal: 8, vertical: 2 },
    },
    stroke: { width: 0 },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      dropShadow: { enabled: false },
      style: { fontSize: isHalf ? "11px" : "12px", fontWeight: 700 },
      background: { enabled: false },
    },
    colors: [colorHex, "#E5E7EB"], // gray-200
    tooltip: {
      y: {
        formatter: (_val, { seriesIndex }) =>
          seriesIndex === 0
            ? `${(v * 100).toFixed(1)}%`
            : `${((1 - v) * 100).toFixed(1)}%`,
      },
    },
    responsive: [
      {
        breakpoint: 640,
        options: {
          legend: { position: "bottom" },
          dataLabels: { style: { fontSize: "11px" } },
        },
      },
    ],
  };

  return (
    <div>
      <div
        className={`w-full rounded-lg border border-gray-200 p-5 mt-4 ${className}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Concentração de Recebimentos
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Participação da maior contraparte (escala 0→0% • 1→100%) nos
              recebimentos do CNPJ analisado
              {periodLabel ? ` • ${periodLabel}` : ""}
            </p>
          </div>
          <Tooltip content={tierHint(tier)}>
            <Badge color={tierBadgeColor(tier)} className="capitalize">
              {tier}
            </Badge>
          </Tooltip>
        </div>

        {/* Orientação muda com layout:
            - full: 2 colunas (gráfico | texto)
            - half: coluna única (gráfico em cima, texto embaixo) */}
        <div
          className={
            isHalf
              ? "mt-5 flex flex-col gap-6 items-stretch"
              : "mt-5 grid grid-cols-2 gap-6 items-center"
          }
        >
          {/* Pie */}
          <div
            className="mx-auto w-full"
            style={{ maxWidth: isHalf ? 320 : 360 }}
          >
            <Chart
              options={chartOptions}
              series={series}
              type="pie"
              width="100%"
            />
          </div>

          {/* Métricas & Insights */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span
                className="inline-flex h-3 w-3 flex-none rounded-full"
                style={{ backgroundColor: colorHex }}
              />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {companyName}
                </p>
                <p className="truncate text-xs text-gray-500">
                  Maior contribuinte de recebimentos
                </p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {pct.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500">do total</p>
              </div>
            </div>

            {typeof totalRecebimentos === "number" && (
              <div className="flex items-center justify-between rounded-xl bg-gray-50 p-3">
                <div className="text-sm text-gray-600">Total no período</div>
                <div className="text-sm font-semibold text-gray-900">
                  {fmtCurrency(totalRecebimentos)}
                </div>
              </div>
            )}

            {typeof topCompanyAmount === "number" && (
              <div className="flex items-center justify-between rounded-xl bg-gray-50 p-3">
                <div className="text-sm text-gray-600">
                  Recebido de {companyName}
                </div>
                <div className="text-sm font-semibold text-gray-900">
                  {fmtCurrency(topCompanyAmount)}
                </div>
              </div>
            )}

            <ContextMessage tier={tier} pct={pct} companyName={companyName} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ContextMessage({ tier, pct, companyName }) {
  const content =
    tier === "baixo" ? (
      <p>
        Concentração <strong>baixa</strong> ({Math.round(pct)}%). A carteira de
        recebíveis está bem distribuída — a principal contraparte ({companyName}
        ) não representa risco relevante.
      </p>
    ) : tier === "medio" ? (
      <p>
        Concentração <strong>média</strong> ({Math.round(pct)}%). Acompanhe a
        dependência da contraparte líder ({companyName}) e considere políticas
        de diversificação.
      </p>
    ) : tier === "alto" ? (
      <p>
        Concentração <strong>alta</strong> ({Math.round(pct)}%). Avalie limites
        de exposição e estratégias para reduzir dependência de {companyName}.
      </p>
    ) : (
      <p>
        Concentração <strong>altíssima</strong> ({Math.round(pct)}%). Risco
        elevado de dependência operacional/financeira de {companyName}.
        Recomenda-se plano imediato de diversificação.
      </p>
    );

  return (
    <div className={`rounded-2xl border p-4 ${messageBorderClass(tier)}`}>
      <div className="flex gap-3">
        <span
          className={`mt-1 h-2.5 w-2.5 flex-none rounded-full ${messageDotClass(
            tier
          )}`}
        />
        <div className="text-sm leading-relaxed text-gray-700">{content}</div>
      </div>
    </div>
  );
}

/* ======================= Helpers ======================= */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
function getTier(v) {
  if (v < 0.3) return "baixo";
  if (v < 0.6) return "medio";
  if (v < 0.8) return "alto";
  return "altissimo";
}
function tierColors(tier) {
  switch (tier) {
    case "baixo":
      return { colorHex: "#22c55e" };
    case "medio":
      return { colorHex: "#f59e0b" };
    case "alto":
      return { colorHex: "#f97316" };
    case "altissimo":
    default:
      return { colorHex: "#dc2626" };
  }
}
function tierBadgeColor(tier) {
  return (
    {
      baixo: "success",
      medio: "warning",
      alto: "warning",
      altissimo: "failure",
    }[tier] || "gray"
  );
}
function tierHint(tier) {
  return {
    baixo: "< 30% concentrado em uma única empresa",
    medio: "30% – 59% concentrado em uma única empresa",
    alto: "60% – 79% concentrado em uma única empresa",
    altissimo: ">= 80% concentrado em uma única empresa",
  }[tier];
}
function fmtCurrency(n) {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(n ?? 0);
  } catch {
    return `R$ ${Number(n || 0).toFixed(2)}`;
  }
}
function messageBorderClass(tier) {
  switch (tier) {
    case "baixo":
      return "border-green-200 bg-green-50";
    case "medio":
      return "border-amber-200 bg-amber-50";
    case "alto":
      return "border-orange-200 bg-orange-50";
    case "altissimo":
    default:
      return "border-red-200 bg-red-50";
  }
}
function messageDotClass(tier) {
  switch (tier) {
    case "baixo":
      return "bg-green-500";
    case "medio":
      return "bg-amber-500";
    case "alto":
      return "bg-orange-500";
    case "altissimo":
    default:
      return "bg-red-600";
  }
}
